import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';

/**
 * BI v2 demo / teyit sayfasının ortak parçaları (27.10.2026) — routes/bi_v2.py (/bi-v2, JWT'li JSON).
 * Not: /bi/report/* uçları BI token'ı ister; JWT orada 401 alır. Bu sayfa yalnız /bi-v2 uçlarını çağırır.
 */

export const KOK = '/bi-v2';
export const ZAMAN_ASIMI = 120000;       // ilk çağrıda rapor üretimi 10-30 sn sürebilir
export const UZUN_ZAMAN_ASIMI = 300000;  // /kpi?yenile=1 beş raporu sırayla yeniden üretir (nginx 300 sn)

const tokenAl = (ctxToken) => {
  if (ctxToken) return ctxToken;
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
};

/** /bi-v2 istemcisi; yanıt gövdesi doğrudan döner (bi_v2.py {data: ...} sarmalamaz). */
export function useBiApi() {
  const { token } = useAuth();
  return useMemo(() => {
    const root = `${API_BASE_URL}${KOK}`;
    const get = (yol, params = {}, timeout = ZAMAN_ASIMI) => {
      const t = tokenAl(token);
      return axios.get(`${root}${yol}`, {
        params,
        timeout,
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      }).then((r) => r.data);
    };
    return {
      get,
      rapor: (key, uc, params, timeout) => get(`/${encodeURIComponent(key)}/${uc}`, params, timeout),
    };
  }, [token]);
}

export const hata = (e) => {
  if (e?.code === 'ECONNABORTED') return 'Zaman aşımı: rapor üretimi beklenenden uzun sürdü, biraz sonra tekrar deneyin.';
  const durum = e?.response?.status;
  const d = e?.response?.data?.detail;
  let metin;
  if (typeof d === 'string') metin = d;
  else if (Array.isArray(d) && d[0]?.msg) metin = d.map((x) => `${(x.loc || []).slice(-1)[0] || ''}: ${x.msg}`).join(' · ');
  else metin = e?.message || 'İstek başarısız.';
  return durum ? `${durum} — ${metin}` : metin;
};

// --- Biçim (tr-TR) ----------------------------------------------------------
const PARA = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
export const para = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v))
  ? '' : `${Number(v).toLocaleString('tr-TR', PARA)} ₺`);
export const sayi = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('tr-TR'));
export const yuzde = (v, basamak = 1) => (v === null || v === undefined || !Number.isFinite(Number(v))
  ? '' : `%${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: basamak, maximumFractionDigits: basamak })}`);
export const tarih = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '');
export const zamanDamgasi = (ts) => (ts ? dayjs.unix(Number(ts)).format('DD.MM.YYYY HH:mm:ss') : '');

/** Kontrol tablosu gibi tipi bilinmeyen sayılar: tam sayı ise binlik ayrılmış, değilse 2 ondalık. */
export const serbestSayi = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v !== 'number') return String(v);
  return Number.isInteger(v) ? v.toLocaleString('tr-TR') : v.toLocaleString('tr-TR', PARA);
};

export const BOS = '(Boş)';
export const degerMetni = (v) => (v === null || v === undefined || v === '' ? BOS : String(v));

export const SAYISAL_TIPLER = new Set(['tutar', 'oran', 'koordinat', 'tamsayi']);

/** Kolon tipine göre hücre biçimi (cerceve.py TIPLER). */
export const tipBicim = (v, tip) => {
  if (v === null || v === undefined || v === '') return '';
  switch (tip) {
    case 'tutar': return para(v);
    case 'oran': return yuzde(v, 2);
    case 'koordinat': return Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 6, maximumFractionDigits: 6 });
    case 'tamsayi': return Number(v).toLocaleString('tr-TR', { useGrouping: false });
    case 'tarih': return tarih(v);
    default: return String(v);
  }
};

export const TIP_GENISLIK = {
  metin: 170, kod: 115, tamsayi: 90, tutar: 150, oran: 105, koordinat: 110, tarih: 105, donem: 90, evet_hayir: 95,
};
export const kolonGenisligi = (k) => {
  if (k.ad === 'Açıklama') return 320;
  if (k.ad === 'Lokasyon Adı' || k.ad === 'Proje Adı') return 240;
  return TIP_GENISLIK[k.tip] || 150;
};

// --- Rapor yuvaları (KPI kartları) -----------------------------------------
// Eşleşme önce kalem_turu, yoksa key ile; tanımlı olmayan yuva "henüz tanımlı değil" kartı olur.
export const RAPOR_YUVALARI = [
  { kalem: 'Masraf', baslik: 'Masraflar', key: 'biv2-masraflar', renk: '#fa8c16' },
  { kalem: 'Gider', baslik: 'Giderler', key: 'biv2-giderler', renk: '#f5222d' },
  { kalem: 'Gelecek Gider', baslik: 'Gelecek Giderler', key: 'biv2-gelecek-giderler', renk: '#ff7875', gelecek: true },
  { kalem: 'Gelir', baslik: 'Gelirler', key: 'biv2-gelirler', renk: '#389e0d' },
  { kalem: 'Gelecek Gelir', baslik: 'Gelecek Gelirler', key: 'biv2-gelecek-gelirler', renk: '#73d13d', gelecek: true },
];
export const EK_RENK = '#2f54eb';
export const NEGATIF_RENK = '#722ed1';

export const DURUM_RENK = { TAMAM: 'green', 'BİLGİ': 'blue', UYARI: 'orange', HATA: 'red' };

/** Süzgeç anahtarları (secenekler sırası); listede olmayan boyut kolonları "Süzgeç ekle" ile açılır. */
export const ANA_SUZGECLER = ['İl', 'İlçe', 'Proje Adı', 'Proje Tipi', 'Proje Alanı', 'Banka', 'Grup', 'Tip', 'Dağıtım', 'Kaynak'];
// Rapor değişince korunanlar: lokasyon boyutları (beş raporda aynı değer kümesi)
export const ORTAK_SUZGECLER = new Set(['İl', 'İlçe', 'Proje Adı', 'Proje Tipi', 'Proje Alanı', 'Banka', 'Lokasyon No',
  'Lokasyon Adı', 'Lokasyon Durumu', 'Aktif Lokasyon']);

/** Lokasyon detay sayfası bağlantısı (yeni sekme). */
export function LokasyonBagi({ no }) {
  if (!no) return null;
  if (String(no) === '0') return no; // depo satırı: lokasyon sayfası yok
  return <a href={`/detay/${encodeURIComponent(no)}`} target="_blank" rel="noreferrer">{no}</a>;
}

export const htmlKacir = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export const yerelOku = (anahtar, varsayilan) => {
  try {
    return localStorage.getItem(anahtar) || varsayilan;
  } catch {
    return varsayilan;
  }
};
export const yerelYaz = (anahtar, deger) => {
  try {
    localStorage.setItem(anahtar, deger);
  } catch { /* gizli pencere */ }
};

/**
 * Sekme etkinken ve istek anahtarı değiştiğinde yükler (gizli sekmeler boşuna istek atmasın).
 * Eski yanıtlar (yarış) yok sayılır. `yeniden()` aynı anahtarla tekrar dener.
 */
export function useAktifYukle(aktif, anahtar, yukleFn) {
  const [durum, setDurum] = useState({ veri: null, yukleniyor: false, hata: null });
  const [tur, setTur] = useState(0);
  const sonAnahtar = useRef(null);
  const istekNo = useRef(0);
  useEffect(() => {
    if (!aktif || !anahtar || sonAnahtar.current === anahtar) return;
    sonAnahtar.current = anahtar;
    istekNo.current += 1;
    const no = istekNo.current;
    setDurum((d) => ({ ...d, yukleniyor: true, hata: null }));
    yukleFn()
      .then((veri) => { if (no === istekNo.current) setDurum({ veri, yukleniyor: false, hata: null }); })
      .catch((e) => { if (no === istekNo.current) setDurum({ veri: null, yukleniyor: false, hata: hata(e) }); });
  }, [aktif, anahtar, tur]); // eslint-disable-line react-hooks/exhaustive-deps
  const yeniden = useCallback(() => {
    sonAnahtar.current = null;
    setTur((t) => t + 1);
  }, []);
  return { ...durum, yeniden };
}
